import { TriggerStep } from "~/lib/pipeline";
import BaseNode from "~/components/Playground/Node/BaseNode";
import { Node, NodeProps } from "@xyflow/react";
import * as HoverCard from "~/components/Playground/Node/StepHoverCard";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { shortCommit } from "~/lib/commits";
import { StepDependencyContext, TriggerJob } from "~/components/build/Show/lib/types";
import Button from "~/components/shared/Button";
import Emojify from "~/components/shared/Emojify";
import { JobDuration } from "~/components/job/Job/JobDuration";
import { StateIcon, StateLabel } from "~/components/Playground/Node/BaseNode/StateIcon";
import { textColorForStep } from "~/components/Playground/Node/colors";
import { NodeDuration } from "~/components/Playground/Node/NodeDuration";
import { memo, useCallback } from "react";
import { MissingStepDependencies } from "~/components/Playground/Node/MissingStepDependencies";
import { useStepDependencies } from "~/components/build/Show/lib/useStepDependencies";
import { useQuery } from "@tanstack/react-query";
import { stepJobsQueryOptions } from "~/components/build/Show/lib/queries/stepJobs";

const TriggerHover = (
  props: NodeProps<TriggerStepNodeType> & {
    job: TriggerJob;
    missingDependencies: StepDependencyContext["missing_dependencies"];
    label: string;
  },
) => {
  const { build } = useBuild({ strict: false });
  const { job } = props;
  // Don't select the node when clicking on the build link
  const handleClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  if (!build || !job || !props.data.state) {
    return null;
  }

  const commit = shortCommit(job.triggerCommit, job.triggerCommitShortLength);
  const pipeline = job?.triggerProject?.name || props.data.triggerPipelineSlug;

  return (
    <div className="space-y-3">
      <div>
        <div className="flex items-baseline gap-2">
          <div className="min-w-0 flex-auto">
            <Emojify text={props.label} className="break-words font-medium" />
          </div>
          <span className="ml-auto flex items-center gap-2">
            <StateLabel {...props.data} missingDependencies={props.missingDependencies} />
            <StateIcon
              className={textColorForStep({
                ...props.data,
                missingDependencies: props.missingDependencies,
              })}
              {...props.data}
              missingDependencies={props.missingDependencies}
            />
          </span>
        </div>
        <div className="flex gap-2">
          {job.startedAt && (
            <span className="whitespace-nowrap">Build #{job.triggeredBuild?.number}</span>
          )}

          <span className="text-charcoal-300">
            {job.triggerAsync ? "(asynchronous)" : <JobDuration job={job} build={build} />}
          </span>
        </div>
      </div>

      <div>
        <span>
          <code className="monospace">{job.triggerBranch}</code>
          {" \x2F "}
          <code className="monospace" title={job.triggerCommit}>
            {commit}
          </code>
        </span>
        {" on "}
        <span className="font-medium">{pipeline}</span>
      </div>

      <MissingStepDependencies missingDependencies={props.missingDependencies} build={build} />

      {job.triggeredBuild && (
        <div>
          <Button
            href={job.triggeredBuild?.canvasUrl}
            theme="small"
            onClick={handleClick}
            target="_blank"
          >
            <span className="after:ml-1 after:content-['→']">Go to build</span>
          </Button>
        </div>
      )}
    </div>
  );
};

type TriggerStepNodeType = Node<TriggerStep, "trigger">;

const useTriggerJob = (step: TriggerStep): TriggerJob | null => {
  const { build } = useBuild();
  const eagerLoadedJobs = build.jobs.filter((job) => job.stepUuid === step.uuid);

  const { data: lazilyLoadedJobs } = useQuery(
    stepJobsQueryOptions(build, step.uuid, { enabled: Features.BuildSidebar }),
  );

  const jobs = Features.BuildSidebar ? lazilyLoadedJobs : eagerLoadedJobs;

  return (jobs?.[jobs.length - 1] as TriggerJob) ?? null;
};

/**
 * Deferred hover content that fetches job data only when mounted.
 * Since this is rendered inside HoverCard.Portal (which lazy-mounts on hover),
 * the fetch only fires when the user actually hovers over the node.
 */
const TriggerHoverContent = ({
  nodeProps,
  missingDependencies,
  label,
}: {
  nodeProps: NodeProps<TriggerStepNodeType>;
  missingDependencies: StepDependencyContext["missing_dependencies"];
  label: string;
}) => {
  const job = useTriggerJob(nodeProps.data);

  if (!job) {
    return null;
  }

  return (
    <TriggerHover
      job={job}
      {...nodeProps}
      missingDependencies={missingDependencies}
      label={label}
    />
  );
};

function TriggerStepNode(props: NodeProps<TriggerStepNodeType>) {
  const { build } = useBuild({ strict: false });
  const { missingDependencies } = useStepDependencies(props.id);

  const label = props.data.label || `Trigger build on ${props.data.triggerPipelineSlug}`;

  const duration =
    Features.BuildCanvasDurations && Features.BuildSidebar && props.data.state ? (
      <NodeDuration
        startedAt={props.data.triggerAsync ? undefined : props.data.statistics?.latestJobStartedAt}
        finishedAt={props.data.statistics?.latestJobFinishedAt}
      />
    ) : undefined;

  return (
    <HoverCard.Root>
      <HoverCard.Portal container={document.getElementById("canvas")}>
        {props.data.state && build && (
          <HoverCard.Content
            state={props.data.state}
            outcome={props.data.outcome}
            missingDependencies={missingDependencies}
            collisionBoundary={document.getElementById("canvas")}
            collisionPadding={10}
          >
            <TriggerHoverContent
              nodeProps={props}
              missingDependencies={missingDependencies}
              label={label}
            />
          </HoverCard.Content>
        )}
      </HoverCard.Portal>

      <HoverCard.Trigger asChild={true}>
        <div>
          <BaseNode {...props} label={<Emojify text={label} />} duration={duration} />
        </div>
      </HoverCard.Trigger>
    </HoverCard.Root>
  );
}

export default memo(TriggerStepNode);
